import { apiGet, apiPost } from "../api-client"

// ============================================================
// TypeScript Interfaces
// ============================================================

/**
 * Request body for bulk plan generation
 */
export interface BulkGeneratePlansRequest {
  well_ids: string[]
  options?: {
    jurisdiction?: "TX" | "NM"
    force_regenerate?: boolean
    plugs_mode?: "combined" | "isolated" | "both"
    input_mode?: "extractions" | "user_files" | "hybrid"
  }
}

/**
 * Request body for bulk status update
 */
export interface BulkUpdateStatusRequest {
  plan_ids: string[]
  new_status: "draft" | "internal_review" | "engineer_approved" | "filed"
}

/**
 * Response from initiating a bulk operation
 */
export interface BulkJobResponse {
  job_id: string
  status: "queued" | "processing" | "completed" | "failed"
  total_wells?: number
  total_plans?: number
  message: string
  estimated_time_seconds?: number
}

/**
 * Detailed status of a bulk job
 */
export interface BulkJob {
  job_id: string
  job_type: "generate_plans" | "update_status"
  status: "queued" | "processing" | "completed" | "failed"
  total_items: number
  processed_items: number
  failed_items: number
  progress_percentage: number
  estimated_time_remaining_seconds?: number
  created_at: string
  started_at?: string
  completed_at?: string
  created_by: string
  result_data?: Record<string, any>
  error_message?: string
}

/**
 * Response from listing bulk jobs
 */
export interface BulkJobsListResponse {
  jobs: BulkJob[]
  total: number
}

/**
 * Query parameters for listing bulk jobs
 */
export interface BulkJobsListParams {
  job_type?: "generate_plans" | "update_status"
  status?: "queued" | "processing" | "completed" | "failed"
  limit?: number
}

// ============================================================
// API Functions
// ============================================================

/**
 * Initiate bulk plan generation for multiple wells
 *
 * @param request - The bulk generation request containing well IDs and options
 * @returns Job response with job_id for tracking progress
 *
 * @example
 * ```typescript
 * const response = await bulkGeneratePlans({
 *   well_ids: ["42301329980000", "42301329990000"],
 *   options: {
 *     jurisdiction: "TX",
 *     plugs_mode: "combined",
 *     force_regenerate: false
 *   }
 * })
 * console.log(`Job ID: ${response.job_id}, Status: ${response.status}`)
 * ```
 */
export async function bulkGeneratePlans(request: BulkGeneratePlansRequest): Promise<BulkJobResponse> {
  console.log("[bulk-api] Initiating bulk plan generation:", request)

  const response = await apiPost<BulkJobResponse>("/api/wells/bulk/generate-plans/", request)

  console.log("[bulk-api] Bulk generation initiated:", response)

  return response
}

/**
 * Update status for multiple plans in bulk
 *
 * @param request - The bulk status update request containing plan IDs and new status
 * @returns Job response with job_id for tracking progress
 *
 * @example
 * ```typescript
 * const response = await bulkUpdatePlanStatus({
 *   plan_ids: ["42301329980000:combined", "42301329990000:isolated"],
 *   new_status: "internal_review"
 * })
 * console.log(`Job ID: ${response.job_id}, Total Plans: ${response.total_plans}`)
 * ```
 */
export async function bulkUpdatePlanStatus(request: BulkUpdateStatusRequest): Promise<BulkJobResponse> {
  console.log("[bulk-api] Initiating bulk status update:", request)

  const response = await apiPost<BulkJobResponse>("/api/plans/bulk/update-status/", request)

  console.log("[bulk-api] Bulk status update initiated:", response)

  return response
}

/**
 * Get the current status of a bulk job
 *
 * @param jobId - The job ID returned from a bulk operation
 * @returns Detailed job status with progress information
 *
 * @example
 * ```typescript
 * const job = await getBulkJobStatus("job_abc123")
 * console.log(`Progress: ${job.progress_percentage}%`)
 * console.log(`Processed: ${job.processed_items}/${job.total_items}`)
 * ```
 */
export async function getBulkJobStatus(jobId: string): Promise<BulkJob> {
  console.log("[bulk-api] Fetching job status:", jobId)

  const response = await apiGet<BulkJob>(`/api/jobs/${jobId}/`)

  console.log("[bulk-api] Job status:", response)

  return response
}

/**
 * List bulk jobs with optional filtering
 *
 * @param params - Optional query parameters for filtering
 * @returns List of bulk jobs matching the criteria
 *
 * @example
 * ```typescript
 * // Get all processing jobs
 * const { jobs } = await listBulkJobs({ status: "processing" })
 *
 * // Get recent plan generation jobs
 * const { jobs } = await listBulkJobs({
 *   job_type: "generate_plans",
 *   limit: 10
 * })
 * ```
 */
export async function listBulkJobs(params?: BulkJobsListParams): Promise<BulkJobsListResponse> {
  console.log("[bulk-api] Listing bulk jobs:", params)

  // Build query string from params
  const queryParams = new URLSearchParams()
  if (params?.job_type) queryParams.append("job_type", params.job_type)
  if (params?.status) queryParams.append("status", params.status)
  if (params?.limit) queryParams.append("limit", params.limit.toString())

  const endpoint = queryParams.toString()
    ? `/api/jobs/?${queryParams.toString()}`
    : "/api/jobs/"

  const response = await apiGet<BulkJobsListResponse>(endpoint)

  console.log("[bulk-api] Jobs list:", response)

  return response
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Poll a bulk job until it completes (success or failure)
 *
 * @param jobId - The job ID to poll
 * @param options - Polling configuration
 * @param options.intervalMs - Milliseconds between poll attempts (default: 2000)
 * @param options.maxAttempts - Maximum number of poll attempts (default: 150, ~5 minutes at 2s interval)
 * @param options.onProgress - Optional callback called on each poll with the current job status
 * @returns The final job status
 * @throws Error if polling times out or job fails
 *
 * @example
 * ```typescript
 * const jobResponse = await bulkGeneratePlans({ well_ids: [...] })
 *
 * const finalJob = await pollJobStatus(jobResponse.job_id, {
 *   intervalMs: 3000,
 *   onProgress: (job) => {
 *     console.log(`Progress: ${job.progress_percentage}%`)
 *     console.log(`ETA: ${job.estimated_time_remaining_seconds}s`)
 *   }
 * })
 *
 * if (finalJob.status === "completed") {
 *   console.log("Job completed successfully!")
 *   console.log("Results:", finalJob.result_data)
 * }
 * ```
 */
export async function pollJobStatus(
  jobId: string,
  options?: {
    intervalMs?: number
    maxAttempts?: number
    onProgress?: (job: BulkJob) => void
  }
): Promise<BulkJob> {
  const intervalMs = options?.intervalMs || 2000
  const maxAttempts = options?.maxAttempts || 150 // 5 minutes at 2s interval

  console.log("[bulk-api] Starting job polling:", jobId, {
    intervalMs,
    maxAttempts,
  })

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const job = await getBulkJobStatus(jobId)

    // Call progress callback if provided
    if (options?.onProgress) {
      options.onProgress(job)
    }

    // Check if job is in a terminal state
    if (job.status === "completed") {
      console.log("[bulk-api] Job completed successfully:", job)
      return job
    }

    if (job.status === "failed") {
      console.error("[bulk-api] Job failed:", job)
      throw new Error(job.error_message || "Bulk job failed")
    }

    // Job is still processing, wait before next poll
    if (attempt < maxAttempts) {
      console.log(
        `[bulk-api] Job ${job.status} (${job.progress_percentage}%), polling again in ${intervalMs}ms...`,
        `(attempt ${attempt}/${maxAttempts})`
      )
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    }
  }

  // Polling timed out
  throw new Error(
    `Bulk job polling timed out after ${maxAttempts} attempts. Job may still be processing.`
  )
}

/**
 * Convenience function to generate plans and wait for completion
 *
 * @param request - The bulk generation request
 * @param pollOptions - Optional polling configuration
 * @returns The completed job with results
 *
 * @example
 * ```typescript
 * const job = await generatePlansAndWait(
 *   {
 *     well_ids: ["42301329980000", "42301329990000"],
 *     options: { jurisdiction: "TX" }
 *   },
 *   {
 *     onProgress: (job) => updateProgressBar(job.progress_percentage)
 *   }
 * )
 *
 * console.log(`Generated ${job.processed_items} plans`)
 * console.log(`Failed: ${job.failed_items}`)
 * ```
 */
export async function generatePlansAndWait(
  request: BulkGeneratePlansRequest,
  pollOptions?: {
    intervalMs?: number
    maxAttempts?: number
    onProgress?: (job: BulkJob) => void
  }
): Promise<BulkJob> {
  console.log("[bulk-api] Generating plans and waiting for completion...")

  const jobResponse = await bulkGeneratePlans(request)

  const finalJob = await pollJobStatus(jobResponse.job_id, pollOptions)

  return finalJob
}

/**
 * Convenience function to update plan status and wait for completion
 *
 * @param request - The bulk status update request
 * @param pollOptions - Optional polling configuration
 * @returns The completed job with results
 *
 * @example
 * ```typescript
 * const job = await updateStatusAndWait(
 *   {
 *     plan_ids: ["42301329980000:combined"],
 *     new_status: "internal_review"
 *   },
 *   {
 *     onProgress: (job) => console.log(`${job.processed_items} updated`)
 *   }
 * )
 * ```
 */
export async function updateStatusAndWait(
  request: BulkUpdateStatusRequest,
  pollOptions?: {
    intervalMs?: number
    maxAttempts?: number
    onProgress?: (job: BulkJob) => void
  }
): Promise<BulkJob> {
  console.log("[bulk-api] Updating status and waiting for completion...")

  const jobResponse = await bulkUpdatePlanStatus(request)

  const finalJob = await pollJobStatus(jobResponse.job_id, pollOptions)

  return finalJob
}
